import React, { useEffect, useRef } from 'react';
import { getFieldValue } from '../../../contentful/mappers';
import Hero from '../Hero/Hero';
import Grid from '../Grid/Grid';
import Accordion from '../Accordion/Accordion';
import Committee from '../Committee/Committee';
import LinksGrid from '../LinksGrid/LinksGrid';
import Custom from '../Custom/Custom';
import SplitCard from '../SplitCard/SplitCard';
import ImagePanels from '../ImagePanels/ImagePanels';
import Stylekit from '../Stylekit/Stylekit';
import GoogleCalendar from '../GoogleCalendar/GoogleCalendar';
import Resources from '../Resources/Resources';
import TextBlock from '../TextBlock/TextBlock';
import './SectionRenderer.css';

// Component registry - maps componentType from CMS to React components
const sectionComponents = {
  hero: Hero,
  grid: Grid,
  accordion: Accordion,
  committeeGrid: Committee,
  linksGrid: LinksGrid,
  customSection: Custom,
  splitCardSection: SplitCard,
  imagePanelSection: ImagePanels,
  stylekitSection: Stylekit,
  googleCalendarSection: GoogleCalendar,
  resourcesSection: Resources,
  textBlockSection: TextBlock,
};

function SectionRenderer({ section, isFirst = false }) {
  const wrapperRef = useRef(null);
  
  // Observe headlines for animation
  useEffect(() => {
    if (!wrapperRef.current) return;

    const headlineSelectors = [
      '.section-headline',
      '.image-panels-headline',
      '.committee-section-headline',
      '.split-card-headline',
      '.hero-headline',
      '.hero-subheadline',
      '.subscribe-headline'
    ];

    const headlines = wrapperRef.current.querySelectorAll(headlineSelectors.join(', '));
    
    if (headlines.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('headline-visible');
            // Unobserve after first visibility to ensure animation only plays once
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    headlines.forEach((headline) => {
      observer.observe(headline);
    });

    return () => {
      observer.disconnect();
    };
  }, [section]);
  
  if (!section || !section.componentType) {
    console.warn('Section missing componentType:', section);
    return (
      <div style={{ padding: '1rem', backgroundColor: '#f8d7da', border: '1px solid #dc3545', margin: '1rem', borderRadius: '4px' }}>
        <p>Section missing componentType</p>
        <pre style={{ fontSize: '0.8rem' }}>{JSON.stringify(section, null, 2)}</pre>
      </div>
    );
  }

  const Component = sectionComponents[section.componentType];

  if (!Component) {
    console.warn(`Unknown section component type: ${section.componentType}`);
    console.warn('Available types:', Object.keys(sectionComponents));
    return (
      <div style={{ padding: '1rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', margin: '1rem', borderRadius: '4px' }}>
        <p>Unknown section component type: <strong>{section.componentType}</strong></p>
        <p style={{ fontSize: '0.9rem' }}>Available types: {Object.keys(sectionComponents).join(', ')}</p>
      </div>
    );
  }
  
  // Check if background pattern should be enabled
  // Default to true for Hero sections (backward compatibility)
  let useBgPattern = false;
  if (section.data) {
    const patternValue = getFieldValue(section.data, 'useBgPattern');
    if (patternValue !== null && patternValue !== undefined) {
      useBgPattern = patternValue;
    }
  }
  
  // Get sectionLink for anchor ID
  const sectionLink = section.data ? getFieldValue(section.data, 'sectionLink') : null;
  const sectionId = sectionLink || null;
  
  const wrapperClass = useBgPattern ? 'section-wrapper has-bg-pattern' : 'section-wrapper';
  
  return (
    <div 
      ref={wrapperRef} 
      id={sectionId || undefined}
      className={wrapperClass}
    >
      <Component data={section.data} isFirst={isFirst} />
    </div>
  );
}

export default SectionRenderer;

